// @flow
import * as React from 'react';
import { translate } from 'react-i18next';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import Radio from 'common/domain/radio';
import Location from 'common/helpers/location';
import PrintingService from 'common/services/PrintingService';
import { getRadio, getToken } from 'statsradio/modules/authentication/session/reducers/sessionReducer';
import Page from 'statsradio/components/page/v2/Page';
import type { WeeklyDashboardData, MonthlyDashboardData, MetricType, Period } from '../domain/DashboardData';
import DashboardApi from '../services/DashboardApi';
import PeriodSelector from './components/DashboardPeriodSelector';
import AudienceComparisonCard from './components/audienceComparison/AudienceComparisonCard';
import DashboardMetricsHeader from './components/metrics/DashboardMetricsHeader';
import DemographyComponent from './components/demography/DemographyComponent';

const ContentWrapper = styled.div`
  display: flex;
  min-height: 500px;

  justify-content: center;
  justify-items: center;
  align-content: center;
  align-items: center;
`;

interface Props {
  radio: Radio;
  token: string;
  theme: Object;
  t: any;
}

type State = {
  audienceMetricType: MetricType,
  period: Period,
  monthly: {
    data?: MonthlyDashboardData,
    isFetching?: boolean,
  },
  weekly: {
    data?: WeeklyDashboardData,
    isFetching?: boolean,
  },
}

class DashboardPage extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);

    this.state = {
      audienceMetricType: 'reach',
      period: 'weekly',
      monthly: {
        data: undefined,
        isFetching: false,
      },
      weekly: {
        data: undefined,
        isFetching: false,
      },
    };
  }

  fetchData() {
    this.fetchWeekly();
    this.fetchMonthly();
  }

  fetchWeekly() {
    const { radio: { callSign }, token } = this.props;
    this.setState({ weekly: { data: undefined, isFetching: true } });

    DashboardApi.getWeeklyDashboardData(callSign, token)
      .then(data => this.setState({ weekly: { data, isFetching: false } }))
      .catch(() => this.setState({ weekly: { isFetching: false } }));
  }

  fetchMonthly() {
    const { radio: { callSign }, token } = this.props;
    this.setState({ monthly: { data: undefined, isFetching: true } });

    DashboardApi.getMonthlyDashboardData(callSign, token)
      .then(data => this.setState({ monthly: { data, isFetching: false } }))
      .catch(() => this.setState({ monthly: { isFetching: false } }));
  }

  componentDidMount() {
    this.fetchData();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.radio !== this.props.radio) {
      this.fetchData();
    }
  }

  getDateRange = () => {
    const { weekly, monthly } = this.state;

    if (this.state.period === 'weekly' && weekly.data) {
      return weekly.data.week;
    } else if (this.state.period === 'monthly' && monthly.data) {
      return monthly.data.month;
    }
    return undefined;
  }

  onMetricChange = (audienceMetricType: MetricType) => this.setState({ audienceMetricType });
  onPeriodChange = (period: Period) => this.setState({ period });

  onPrint = () => {
    const { period, audienceMetricType } = this.state;
    const { radio } = this.props;
    const dateRange = this.getDateRange();

    if (!dateRange) {
      return;
    }

    const url = Location.buildUrlFor('dashboard-printing');

    const message = {
      data: this.state[period].data,
      from: dateRange.from,
      to: dateRange.to,
      monthlySelected: period === 'monthly',
      type: audienceMetricType,
      radio: radio,
    };

    PrintingService.printWindowWithData(url, 'DASHBOARD_PRINTING_DATA', message);
  };

  buildPageMessages = () => {
    const { t } = this.props;
    const { period } = this.state;
    const metrics = this.state[period];

    const messages = [];
    const showMissingSampling = !metrics.isFetching && metrics.data && metrics.data.isInterpolated;
    if (showMissingSampling) {
      messages.push({ message: t('metrics:missingSampling'), type: 'important' });
    }

    const showFallbackWarning = !metrics.isFetching && metrics.data && metrics.data.isAFallback;
    if (showFallbackWarning) {
      messages.push({ message: `${t('metricsAreAFallback')} ${t('ifProblemPersistsContactUs')}`, type: 'important' });
    }

    messages.unshift({ message: <NavLink to="/dashboard">/dashboard</NavLink>, type: 'important' });
    return messages;
  };

  render() {
    const { t } = this.props;
    const { period } = this.state;
    const { isFetching, data } = this.state[period];
    const dateRange = this.getDateRange();

    let periodText = '';

    if (dateRange) {
      periodText = `${t('date.text', { value: dateRange.from })} - ${t('date.text', { value: dateRange.to })}`;
    }

    const hasNoData = !isFetching && !data;
    return (
      <Page
        extraRightAction={
          <PeriodSelector
            onChange={this.onPeriodChange}
            value={period}
          />
        }
        helmetTitle={t('dashboard')}
        isLoading={isFetching}
        messages={this.buildPageMessages()}
        onPrint={this.onPrint}
        title={periodText}
      >
        { hasNoData &&
          <ContentWrapper><h3>{t('dashboardError')}</h3></ContentWrapper>
        }
        { !hasNoData &&
          <React.Fragment>
            <DashboardMetricsHeader period={period} metrics={data} />
            <AudienceComparisonCard data={data} period={period} onMetricChange={this.onMetricChange} />
            <DemographyComponent data={data} />
          </React.Fragment>
        }
      </Page>
    );
  }
}

function mapStateToProps(state) {
  return {
    radio: getRadio(state),
    token: getToken(state),
  };
}

export default translate('dashboard', 'metrics')(connect(mapStateToProps)(DashboardPage));



// WEBPACK FOOTER //
// ./src/statsradio/modules/dashboard/v2/DashboardPage.js